import { API_URL } from "../config";
import { isInstructor } from "./roles";

/**
 * @param {unknown} value
 * @returns {string}
 */
function readNonEmptyString(value) {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : "";
}

/**
 * Course docs may arrive populated, as a nested `curso`, or with `nombre` instead of `titulo`.
 * @param {object} curso
 * @returns {object}
 */
function unwrapCourseRecord(curso) {
  if (curso.curso && typeof curso.curso === "object" && !Array.isArray(curso.curso)) {
    return curso.curso;
  }
  return curso;
}

/**
 * @param {object} curso
 * @returns {string}
 */
function readCourseTitle(curso) {
  const record = unwrapCourseRecord(curso);
  return (
    readNonEmptyString(record.titulo)
    || readNonEmptyString(record.nombre)
    || readNonEmptyString(curso.titulo)
    || readNonEmptyString(curso.nombre)
  );
}

/**
 * @param {object} curso
 * @returns {string}
 */
function readCourseId(curso) {
  const record = unwrapCourseRecord(curso);
  const rawId = record._id ?? record.id ?? curso.cursoId ?? curso._id ?? curso.id;
  if (rawId == null) {
    return "";
  }
  return String(rawId).trim();
}

/**
 * Normalizes GET /perfil cursosInscritos for the profile UI (populated docs, plain ids, or nulls from stale refs).
 * @param {unknown} cursosInscritos
 * @returns {Array<{ id: string; titulo: string }>}
 */
export function mapCursosInscritosForPerfil(cursosInscritos) {
  if (!Array.isArray(cursosInscritos)) {
    return [];
  }
  /** @type {Array<{ id: string; titulo: string }>} */
  const out = [];
  for (const curso of cursosInscritos) {
    if (curso == null) {
      continue;
    }
    if (typeof curso === "object" && !Array.isArray(curso)) {
      const id = readCourseId(curso);
      if (id === "") {
        continue;
      }
      out.push({ id, titulo: readCourseTitle(curso) });
      continue;
    }
    const id = String(curso).trim();
    if (id === "") {
      continue;
    }
    out.push({ id, titulo: "" });
  }
  return out;
}

/**
 * @returns {Promise<Array<{ id: string; titulo: string }>>}
 */
async function fetchAssignedCourseCatalog() {
  try {
    const response = await fetch(`${API_URL}/cursos/lista-basica`, { credentials: "include" });
    if (!response.ok) {
      return [];
    }
    const body = await response.json();
    return mapCursosInscritosForPerfil(body);
  } catch {
    return [];
  }
}

/**
 * Students use cursosInscritos. Instructors with an empty enrollment list use the courses
 * where they are administrators (`/cursos/lista-basica`). Plain ids are matched to titles.
 * @param {object | null | undefined} profile
 * @returns {Promise<Array<{ id: string; titulo: string }>>}
 */
export async function loadCoursesForPerfil(profile) {
  const enrolled = mapCursosInscritosForPerfil(profile?.cursosInscritos);
  const role = typeof profile?.rol === "string" ? profile.rol : "";
  const missingTitle = enrolled.some((course) => course.titulo === "");
  const instructorWithoutCourses = isInstructor(role) && enrolled.length === 0;
  if (!missingTitle && !instructorWithoutCourses) {
    return enrolled;
  }

  const catalog = await fetchAssignedCourseCatalog();
  if (instructorWithoutCourses) {
    return catalog;
  }

  return enrolled.map((course) => {
    if (course.titulo) {
      return course;
    }
    const match = catalog.find((item) => item.id === course.id && item.titulo);
    return match ? { id: course.id, titulo: match.titulo } : course;
  });
}
